import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdNotifications, MdNotificationsNone, MdOpenInNew } from 'react-icons/md';
import { useLeadNotifications, NotifLead } from 'hooks/useLeadNotifications';

function timeAgo(iso: string): string {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
}

function LeadItem({ lead, onClick }: { lead: NotifLead; onClick: () => void }) {
    const price = lead.customerRate != null
        ? `${lead.customerRate.toFixed(2)} €`
        : lead.leadrsRate != null
        ? `${lead.leadrsRate.toFixed(2)} €`
        : null;

    return (
        <button
            onClick={onClick}
            className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-leadrs-stroke"
        >
            <span className="mt-1 flex h-2 w-2 flex-shrink-0 rounded-full bg-leadrs-success" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-leadrs-text">
                    Nouveau lead
                    {lead.offerBuyerNumber != null && (
                        <span className="ml-1 font-normal text-leadrs-text-soft">
                            #{lead.offerBuyerNumber}
                        </span>
                    )}
                </p>
                <div className="mt-0.5 flex items-center gap-2">
                    {price && (
                        <span className="rounded-full bg-leadrs-green-soft px-2 py-0.5 text-[11px] font-medium text-leadrs-green-focus">
                            {price}
                        </span>
                    )}
                    <span className="text-[11px] text-leadrs-text-soft">
                        {lead.createdAt ? timeAgo(lead.createdAt) : ''}
                    </span>
                </div>
            </div>
        </button>
    );
}

export default function NotificationBell() {
    const { newLeads, unreadCount, markAsSeen } = useLeadNotifications();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const userType = localStorage.getItem('user-type');
    if (userType !== 'buyer') return null;

    const handleOpen = () => setOpen((prev) => !prev);

    const handleClose = () => {
        setOpen(false);
        markAsSeen();
    };

    const handleLeadClick = () => {
        handleClose();
        navigate('/leads');
    };

    const visibleLeads = newLeads.slice(0, 5);

    return (
        <div ref={wrapperRef} className="relative">
            {/* Bell button */}
            <button
                onClick={handleOpen}
                className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg shadow-shadow-500 transition-colors hover:bg-leadrs-stroke"
                aria-label="Notifications"
            >
                {unreadCount > 0 ? (
                    <MdNotifications size={22} color="#1C0F53" />
                ) : (
                    <MdNotificationsNone size={22} color="#676767" />
                )}
                {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {open && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={handleClose}
                    />
                    <div className="absolute right-0 top-12 z-50 w-80 rounded-2xl border border-leadrs-stroke bg-white p-3 shadow-2xl">
                        {/* Header */}
                        <div className="mb-2 flex items-center justify-between px-1">
                            <span className="text-[13px] font-semibold text-leadrs-text">
                                Notifications
                            </span>
                            {unreadCount > 0 && (
                                <span className="rounded-full bg-leadrs-purple-soft px-2 py-0.5 text-[11px] font-medium text-leadrs-purple-focus">
                                    {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
                                </span>
                            )}
                        </div>

                        {/* Lead list */}
                        {visibleLeads.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 py-6 text-center">
                                <MdNotificationsNone size={32} color="#A3AED0" />
                                <p className="text-[13px] text-leadrs-text-soft">
                                    Aucun nouveau lead
                                </p>
                                <p className="text-[11px] text-leadrs-text-soft">
                                    Vous serez alerté dès qu'un lead arrive.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {visibleLeads.map((lead) => (
                                    <LeadItem
                                        key={lead._id}
                                        lead={lead}
                                        onClick={handleLeadClick}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="mt-2 border-t border-leadrs-stroke pt-2">
                            <button
                                onClick={handleLeadClick}
                                className="flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-[13px] font-medium text-leadrs-brand transition-colors hover:bg-leadrs-purple-soft"
                            >
                                Voir tous mes leads
                                <MdOpenInNew size={14} />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
